import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FileText, Loader2, Sparkles, Link2, Upload, Trash2 } from 'lucide-react'
import { MEETING_DOCUMENT_ACCEPT, MEETING_DOCUMENT_FORMATS_HINT } from '../../constants/meetingDocuments'
import { meetingsAPI, debugPrepAttachments, viteSurfaceScreenshare404Details } from '../../services/api'
import { useToast } from '../common/Toast'
import AgendaDocumentMappingPanel from './AgendaDocumentMappingPanel'

function unwrapData(res) {
  return res?.data ?? res
}

/** User-facing text for failed agenda-suggestion API calls. */
function agendaSuggestionsErrorMessage(err) {
  if (!err || typeof err !== 'object') return 'Request failed.'
  const status = err.status
  const code = err.code
  const message = typeof err.message === 'string' ? err.message : 'Request failed.'
  if (code === 'ATTACHMENT_AGENDA_SUGGESTIONS_DISABLED') return message
  if (code === 'FORBIDDEN' || status === 403) {
    return `${message} (You can still use meeting documents listed here.)`
  }
  if (code === 'NOT_FOUND' || status === 404) return message
  if (status === 503) return message
  return message
}

/** Last screenshare PUT 404 diagnostic surface — only populated when backend sends `screenshareStatusLookupMiss` AND `VITE_SURFACE_SCREENSHARE_404_DETAILS=true`. Inspect `window.__RITHMO_SCREENSHARE_LOOKUP_MISS` in Console. */
function assignWindowScreenshare404Debug(body) {
  if (typeof window === 'undefined' || !viteSurfaceScreenshare404Details()) return
  const miss = body?.error?.details?.screenshareStatusLookupMiss
  if (miss && typeof miss === 'object') {
    window.__RITHMO_SCREENSHARE_LOOKUP_MISS = { surfacedAtIso: new Date().toISOString(), ...miss }
  }
}

/** Public processing status — list/upload use `toPublicAttachment` (`brief_ready`→`processed`); tolerate raw `brief_ready` if seen. */
function normalizeAttachmentProcessingPublic(att) {
  const raw = att?.processingStatus ?? att?.processing_status
  if (raw === 'brief_ready') return 'processed'
  return raw
}

function isPdfAttachment(att) {
  if (!att || typeof att !== 'object') return false
  const ctype = String(att.contentType ?? att.content_type ?? '')
  const name = String(att.filename ?? '')
  return ctype === 'application/pdf' || /\.pdf$/i.test(name)
}

function attachmentScreenshareStatus(att) {
  return att?.screenshareStatus ?? att?.screenshare_status ?? 'not_decided'
}

function pdfHasRenderablePageCount(att) {
  const pc = att.pageCount ?? att.page_count
  if (pc == null) return false
  const n = Number(pc)
  return !Number.isNaN(n) && n > 0
}

/**
 * Approval modal ONLY when extraction is done (`processed`), screenshare unset, and PDFs have page_count for slide share.
 */
function attachmentScreenshareReadyForPrompt(att) {
  if (!att || typeof att !== 'object') return false
  const ps = normalizeAttachmentProcessingPublic(att)
  if (ps !== 'processed') return false
  if (attachmentScreenshareStatus(att) !== 'not_decided') return false
  if (!isPdfAttachment(att)) return true
  return pdfHasRenderablePageCount(att)
}

const POLL_INTERVAL_MS = 800
/** Hard cap ~60s regardless of timers below. */
const MAX_SHARE_POLL_TICKS = 76
/** Wall-clock stop for this wait session. */
const SHARE_POLL_DEADLINE_MS = 75_000
/** Extra wait after PDF is `processed` for async page render — then stop polling if still no pages. */
const PDF_PAGE_PREVIEW_GRACE_MS = 34_000

/** User-visible line under spinner during shareApprovalWaitId (cleared when polling stops). */
function shareApprovalWaitSubtitle(att, firstProcessedAtMsValue) {
  if (!att || typeof att !== 'object') return 'Finding your uploaded document…'
  const ps = normalizeAttachmentProcessingPublic(att)
  if (ps == null || ps === '') return 'Loading attachment details…'
  if (ps === 'failed') return ''
  if (attachmentScreenshareStatus(att) !== 'not_decided') return 'Sharing preference already set for this attachment.'
  if (ps === 'pending' || ps === 'extracting') return 'Processing document for sharing options…'
  if (ps !== 'processed') return 'Processing document for sharing options…'
  if (isPdfAttachment(att)) {
    if (pdfHasRenderablePageCount(att)) return ''
    const t0 = firstProcessedAtMsValue
    const ageMs = typeof t0 === 'number' ? Date.now() - t0 : 0
    if (!t0 || ageMs < 1500) return 'Document processed — waiting for preview details…'
    return 'Document processed — waiting for slide previews (required before sharing)…'
  }
  return ''
}

function approvalAttachmentFromListRow(att, filenameFallback) {
  return {
    id: att.id,
    filename: att.filename || filenameFallback || att.id,
    pageCount: att.pageCount ?? att.page_count ?? null,
    contentType: att.contentType ?? att.content_type ?? null,
  }
}

/** Backend upload scope: series-wide vs single meeting / occurrence (matches listScope intent). */
function uploadScopeFromListScope(listScope) {
  if (listScope === 'series') return 'series'
  return 'meeting'
}

/**
 * Meeting prep materials: upload, list, delete, and optional agenda link suggestions derived on the server from prep data (no prompt-time LLM).
 */
export default function DocumentIntelligencePanel({
  meetingId,
  listScope = 'effective',
  title = 'Meeting prep materials',
  /** Overview / builder: upload controls only; full lists stay on Pre-read tab. */
  variant = 'full',
  /** Shown under the title when variant is upload-only (set null to hide). */
  uploadOnlyHint = 'For the full prep library and agenda ideas, open the Agenda tab.',
  /** `false` hides Generate ideas + suggested-links UI (same meeting may use multiple panels). */
  showAgendaSuggestions = true,
  /** Called after agenda-apply succeeds (e.g. refresh meeting agenda). */
  onAfterApply,
  /** Called after attachments are uploaded, removed, or sharing status changes. */
  onAttachmentsMutated,
  /** Optional `input[type=file]` accept list (defaults to standard office formats). */
  accept = MEETING_DOCUMENT_ACCEPT,
  /** Shown under upload controls (defaults to supported formats line). */
  formatsHint = MEETING_DOCUMENT_FORMATS_HINT,
  /** Tighter layout for meeting builder step 8. */
  compact = false,
  /** Single upload control with no panel chrome (e.g. agenda tab header). */
  inlineButtonLabel = 'Upload Document',
}) {
  const isUploadOnly = variant === 'upload-only'
  const isInlineButton = variant === 'inline-button'
  const showTitle = Boolean(title && String(title).trim()) && !isInlineButton
  const toast = useToast()
  const toastRef = useRef(toast)
  toastRef.current = toast
  const errorShownRef = useRef(false)
  const [disabled, setDisabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [attachments, setAttachments] = useState([])
  const [links, setLinks] = useState([])
  const [busy, setBusy] = useState(false)
  const [selectedApplyIds, setSelectedApplyIds] = useState(() => new Set())
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [shareUploadWithAll, setShareUploadWithAll] = useState(false)
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null)
  const [approvalAttachment, setApprovalAttachment] = useState(null)
  const [mappingAttachment, setMappingAttachment] = useState(null)
  /** After upload: wait until list shows processed + PDF pages before opening share modal. */
  const [shareApprovalWaitId, setShareApprovalWaitId] = useState(null)
  const shareApprovalWaitFilenameRef = useRef('')
  /** First time polled row hit `processed` — PDF can stay `processed` with null page_count until/unless render completes. */
  const shareWaitFirstProcessedAtMsRef = useRef(null)
  /** PDF processed but page render never produced rows — polling stopped; clears on new upload / meeting. */
  const [sharePrepStaleNotice, setSharePrepStaleNotice] = useState(null)

  const refreshAttachments = useCallback(async () => {
    if (!meetingId) return
    try {
      const res = await meetingsAPI.listAttachments(meetingId, listScope)
      const data = unwrapData(res)
      const list = data?.attachments ?? []
      setAttachments(Array.isArray(list) ? list : [])
      setDisabled(false)
      errorShownRef.current = false
    } catch (e) {
      const status = e?.status
      if (status === 404) {
        setDisabled(true)
        setAttachments([])
      } else if (!errorShownRef.current) {
        errorShownRef.current = true
        toastRef.current?.error('Could not load attachments', e?.message || 'Request failed')
      }
    }
  }, [meetingId, listScope])

  const refreshLinks = useCallback(async () => {
    if (!meetingId || !showAgendaSuggestions) return
    try {
      const res = await meetingsAPI.listAttachmentAgendaLinks(meetingId)
      const data = unwrapData(res)
      const list = data?.links ?? []
      setLinks(Array.isArray(list) ? list : [])
    } catch (e) {
      if (e?.status === 404) {
        setLinks([])
      }
    }
  }, [meetingId, showAgendaSuggestions])

  useEffect(() => {
    errorShownRef.current = false
    let cancelled = false
    ;(async () => {
      setLoading(true)
      await refreshAttachments()
      await refreshLinks()
      if (!cancelled) setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [refreshAttachments, refreshLinks])

  useEffect(() => {
    setApprovalAttachment(null)
    setMappingAttachment(null)
    setShareApprovalWaitId(null)
    shareApprovalWaitFilenameRef.current = ''
    shareWaitFirstProcessedAtMsRef.current = null
    setSharePrepStaleNotice(null)
  }, [meetingId, listScope])

  useEffect(() => {
    if (!shareApprovalWaitId || !meetingId) return undefined
    const waitId = shareApprovalWaitId
    const filenameFallback = shareApprovalWaitFilenameRef.current
    const deadline = Date.now() + SHARE_POLL_DEADLINE_MS
    let cancelled = false
    let ticks = 0

    const clearWaitState = (reason) => {
      if (import.meta.env.DEV) {
        // Helps trace why the warning line / spinner disappeared (multi-exit polling).
        console.debug('[rithmo-share-poll] wait_cleared', {
          reason,
          meetingId,
          listScope,
          attachmentId: waitId,
          ticks,
          deadlineExceeded: Date.now() > deadline,
        })
      }
      setShareApprovalWaitId(null)
      shareApprovalWaitFilenameRef.current = ''
      shareWaitFirstProcessedAtMsRef.current = null
    }

    const poll = async () => {
      if (cancelled) return
      ticks += 1
      if (ticks > MAX_SHARE_POLL_TICKS || Date.now() > deadline) {
        debugPrepAttachments('share_prompt_poll_hard_stop', {
          meetingId,
          listScope,
          attachmentId: waitId,
          ticks,
          reason: ticks > MAX_SHARE_POLL_TICKS ? 'max_ticks' : 'deadline',
        })
        toastRef.current?.info(
          'Sharing prompt stopped',
          'Reload this tab or retry upload if previews never appear — polling was capped to avoid extra API load.',
        )
        clearWaitState('poll_cap_max_ticks_or_deadline')
        await refreshAttachments()
        return
      }

      try {
        const res = await meetingsAPI.listAttachments(meetingId, listScope)
        const data = unwrapData(res)
        const list = Array.isArray(data?.attachments) ? data.attachments : []
        setAttachments(list)
        const att = list.find((a) => a?.id === waitId)

        const stopProcessedPdfNeverShareable = () => {
          setSharePrepStaleNotice(
            `“${filenameFallback || 'This PDF'}”: document processed, but sharing pages are not ready (no slide preview rows yet — page render may still be running or unavailable). Screenshare approval stayed closed.`,
          )
          toastRef.current?.info(
            'Sharing pages not ready',
            'The file is processed, but slide-based sharing needs page previews. Retry later or fix PDF rendering on the server.',
          )
          debugPrepAttachments('share_prompt_stopped_pdf_no_renderable_pages', {
            attachmentId: waitId,
            pageCount: att?.pageCount ?? att?.page_count,
          })
          clearWaitState('processed_pdf_no_page_previews_after_grace')
        }

        if (!att) {
          if (ticks === 1 || ticks % 10 === 0) {
            debugPrepAttachments('share_prompt_poll_row_missing', {
              meetingId,
              listScope,
              attachmentId: waitId,
              tick: ticks,
            })
          }
          return
        }

        if (normalizeAttachmentProcessingPublic(att) === 'failed' || att.processing_status === 'failed') {
          debugPrepAttachments('share_prompt_wait_failed_pipeline', { attachmentId: waitId })
          toastRef.current?.error(
            'Document processing failed',
            att.processingErrorCode || att.processing_error_code
              ? String(att.processingErrorCode || att.processing_error_code)
              : 'Prep could not finish for this file.',
          )
          clearWaitState('pipeline_failed')
          return
        }

        if (normalizeAttachmentProcessingPublic(att) === 'processed') {
          shareWaitFirstProcessedAtMsRef.current ||= Date.now()
        }

        if (normalizeAttachmentProcessingPublic(att) === 'processed' && attachmentScreenshareStatus(att) !== 'not_decided') {
          debugPrepAttachments('share_prompt_cleared_screenshare_already_set', { attachmentId: waitId })
          clearWaitState('screenshare_already_decided')
          return
        }

        const pcVal = att.pageCount ?? att.page_count

        if (attachmentScreenshareReadyForPrompt(att)) {
          debugPrepAttachments('share_prompt_attachment_ready', {
            meetingId,
            attachmentId: waitId,
            pageCount: pcVal,
          })
          setSharePrepStaleNotice(null)
          setApprovalAttachment(approvalAttachmentFromListRow(att, filenameFallback))
          clearWaitState('attachment_screenshare_ready_for_prompt')
          return
        }

        if (
          normalizeAttachmentProcessingPublic(att) === 'processed' &&
          isPdfAttachment(att) &&
          attachmentScreenshareStatus(att) === 'not_decided' &&
          !pdfHasRenderablePageCount(att) &&
          shareWaitFirstProcessedAtMsRef.current != null &&
          Date.now() - shareWaitFirstProcessedAtMsRef.current >= PDF_PAGE_PREVIEW_GRACE_MS
        ) {
          stopProcessedPdfNeverShareable()
          return
        }
      } catch (e) {
        if (ticks <= 2) {
          debugPrepAttachments('share_prompt_poll_error', { attachmentId: waitId, message: e?.message })
        }
      }
    }

    const iv = window.setInterval(() => void poll(), POLL_INTERVAL_MS)
    void poll()
    return () => {
      cancelled = true
      window.clearInterval(iv)
    }
  }, [shareApprovalWaitId, meetingId, listScope, refreshAttachments])

  const removeAttachment = async (attachmentId, filename) => {
    const label = filename || 'this document'
    if (!window.confirm(`Remove “${label}” from meeting prep? This cannot be undone.`)) return
    setDeletingAttachmentId(attachmentId)
    try {
      await meetingsAPI.deleteAttachment(meetingId, attachmentId)
      toast.success('Document removed', label)
      await refreshAttachments()
      await refreshLinks()
      onAttachmentsMutated?.()
    } catch (e) {
      toast.error('Remove failed', e?.message || 'Request failed')
    } finally {
      setDeletingAttachmentId(null)
    }
  }

  const runSuggestions = async () => {
    setBusy(true)
    try {
      const res = await meetingsAPI.postAgendaSuggestionsFromAttachments(meetingId, { listScope })
      const data = unwrapData(res)
      const inserted = Number(data?.inserted ?? 0)
      if (inserted > 0) {
        toast.success('Suggestions updated', `${inserted} link(s) from meeting prep`)
      } else {
        toast.info(
          'No links generated',
          'No usable prep-derived agenda ideas were produced yet (documents may still be processing, or suggested ideas require a rebuilt prep pipeline).',
        )
      }
      await refreshLinks()
    } catch (e) {
      toast.error('Agenda suggestions', agendaSuggestionsErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const toggleApply = (linkId) => {
    setSelectedApplyIds((prev) => {
      const next = new Set(prev)
      if (next.has(linkId)) next.delete(linkId)
      else next.add(linkId)
      return next
    })
  }

  const applySelected = async () => {
    const ids = [...selectedApplyIds]
    if (ids.length === 0) {
      toast.error('Apply', 'Select at least one suggested link')
      return
    }
    setBusy(true)
    try {
      await meetingsAPI.postAgendaSuggestionsApply(meetingId, ids)
      toast.success('Agenda updated', 'New items were appended (existing items unchanged).')
      setSelectedApplyIds(new Set())
      await refreshLinks()
      onAfterApply?.()
    } catch (e) {
      toast.error('Apply', e?.message || 'Failed')
    } finally {
      setBusy(false)
    }
  }

  const patchLink = async (linkId, linkType) => {
    if (linkType === 'rejected') {
      setSelectedApplyIds((prev) => {
        const next = new Set(prev)
        next.delete(linkId)
        return next
      })
    }
    setBusy(true)
    try {
      await meetingsAPI.patchAttachmentAgendaLink(meetingId, linkId, { linkType })
      await refreshLinks()
    } catch (e) {
      toast.error('Update link', e?.message || 'Failed')
    } finally {
      setBusy(false)
    }
  }

  const handleApprovalDecision = async (status) => {
    if (!approvalAttachment) return
    const stillListed = attachments.some((a) => a?.id === approvalAttachment.id)
    if (!stillListed) {
      debugPrepAttachments('screenshare_status_skipped_stale_attachment', {
        meetingId,
        listScope,
        attachmentId: approvalAttachment.id,
      })
      toast.error(
        'Sharing choice expired',
        'This document is not in the current prep list for this meeting view. Close the dialog and try again.',
      )
      setApprovalAttachment(null)
      return
    }
    const live = attachments.find((a) => a?.id === approvalAttachment.id)
    if (!live || !attachmentScreenshareReadyForPrompt(live)) {
      debugPrepAttachments('screenshare_status_skipped_not_ready', {
        meetingId,
        attachmentId: approvalAttachment.id,
        processingStatus: live?.processingStatus ?? live?.processing_status,
        normalized: live ? normalizeAttachmentProcessingPublic(live) : null,
        pageCount: live?.pageCount ?? live?.page_count,
        screenshareStatus: live ? attachmentScreenshareStatus(live) : null,
      })
      toast.error(
        'Preview not ready yet',
        'Wait until this document shows processed with slide previews (PDFs), then try sharing again.',
      )
      setApprovalAttachment(null)
      return
    }
    try {
      await meetingsAPI.updateScreenshareStatus(meetingId, approvalAttachment.id, status)
      onAttachmentsMutated?.()
      if (status === 'approved_for_meeting_share') {
        setMappingAttachment(approvalAttachment)
      }
    } catch (err) {
      assignWindowScreenshare404Debug(err?.body)
      toast.error('Could not save sharing decision', err?.message || 'Request failed')
    } finally {
      setApprovalAttachment(null)
    }
  }

  const onUploadFileChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !meetingId) return
    setUploading(true)
    try {
      const scope = uploadScopeFromListScope(listScope)
      await meetingsAPI.uploadAttachment(meetingId, file, {
        scope,
        // Screenshare sharing not ready for primetime — never share on upload.
        shareWithAllParticipants: false,
      })
      toast.success('Document uploaded', file.name)
      await refreshAttachments()
      onAttachmentsMutated?.()
      // Screenshare sharing disabled — skip the post-upload "share during
      // meeting?" prompt. Uploads are kept for agenda generation only.
      // const uploadData = uploadRes?.data ?? uploadRes
      // const newAttachment = uploadData?.attachment
      // if (newAttachment?.id) {
      //   setSharePrepStaleNotice(null)
      //   shareApprovalWaitFilenameRef.current = newAttachment.filename || file.name
      //   shareWaitFirstProcessedAtMsRef.current = null
      //   if (attachmentScreenshareReadyForPrompt(newAttachment)) {
      //     setApprovalAttachment(approvalAttachmentFromListRow(newAttachment, file.name))
      //   } else {
      //     setShareApprovalWaitId(newAttachment.id)
      //     toastRef.current?.info(
      //       'Preparing preview',
      //       'We will ask about sharing when prep finishes (PDFs wait for slide previews when available).',
      //     )
      //   }
      // }
    } catch (err) {
      const message =
        typeof err?.message === 'string' && err.message.trim()
          ? err.message
          : 'Request failed.'
      toast.error('Upload failed', message)
    } finally {
      setUploading(false)
    }
  }

  const suggestedAgendaLinks = links.filter((l) => l.linkType === 'suggested')

  const shellClass = compact
    ? 'rounded-xl border border-gray-100 bg-transparent p-0'
    : 'glass rounded-2xl p-6'

  if (disabled) {
    if (isInlineButton) return null
    return (
      <div className={`${shellClass} border border-dashed border-gray-200`}>
        {showTitle ? (
          <h3 className="text-lg font-semibold text-gray-700 mb-1 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-400" />
            {title}
          </h3>
        ) : null}
        <p className="text-sm text-gray-500">
          Could not load meeting prep materials (API returned not found). Check that the server is running and{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">VITE_API_URL</code> ends with{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">/api</code> (e.g.{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">http://localhost:5000/api</code>).
        </p>
      </div>
    )
  }

  const showIdeasChrome = showAgendaSuggestions && !isUploadOnly && !isInlineButton

  const waitingShareRow =
    shareApprovalWaitId ? attachments.find((a) => a?.id === shareApprovalWaitId) : null
  const shareApprovalWaitBannerLine =
    shareApprovalWaitId &&
    waitingShareRow &&
    shareApprovalWaitSubtitle(waitingShareRow, shareWaitFirstProcessedAtMsRef.current)

  const dismissSharePrepStaleNotice = () => setSharePrepStaleNotice(null)

  if (isInlineButton) {
    return (
      <>
        {sharePrepStaleNotice ? (
          <div
            className="fixed bottom-4 right-4 z-40 max-w-sm rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-gray-800 shadow-lg"
            role="status"
          >
            <span>{sharePrepStaleNotice}</span>
            <button type="button" className="btn-ghost text-sm mt-2" onClick={dismissSharePrepStaleNotice}>
              Dismiss
            </button>
          </div>
        ) : null}
        {approvalAttachment && createPortal(
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <h3 className="text-lg font-semibold mb-2">Share this document during the meeting?</h3>
              <p className="text-sm text-gray-500 mb-4">{approvalAttachment.filename}</p>
              <div className="flex gap-3 justify-end flex-wrap">
                <button
                  type="button"
                  onClick={() => handleApprovalDecision('do_not_share')}
                  className="btn-ghost text-sm text-gray-600"
                >
                  No, keep private
                </button>
                <button
                  type="button"
                  onClick={() => handleApprovalDecision('not_decided')}
                  className="btn-secondary text-sm"
                >
                  Decide later
                </button>
                <button
                  type="button"
                  onClick={() => handleApprovalDecision('approved_for_meeting_share')}
                  className="btn-primary text-sm"
                >
                  Yes, share during meeting
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
        {mappingAttachment && (
          <AgendaDocumentMappingPanel
            meetingId={meetingId}
            attachmentId={mappingAttachment.id}
            filename={mappingAttachment.filename}
            pageCount={mappingAttachment.pageCount}
            onClose={() => setMappingAttachment(null)}
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          className="sr-only"
          aria-label="Upload meeting document"
          onChange={onUploadFileChange}
        />
        <button
          type="button"
          disabled={busy || uploading || Boolean(shareApprovalWaitId)}
          onClick={() => fileInputRef.current?.click()}
          className="btn-secondary inline-flex items-center gap-2 text-sm disabled:opacity-60"
        >
          {uploading || shareApprovalWaitId ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {inlineButtonLabel}
        </button>
      </>
    )
  }

  return (
    <div className={shellClass}>
      {sharePrepStaleNotice ? (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-gray-800 mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2"
          role="status"
        >
          <span className="min-w-0">{sharePrepStaleNotice}</span>
          <button
            type="button"
            className="btn-ghost text-sm shrink-0 self-end sm:self-start"
            onClick={dismissSharePrepStaleNotice}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {/* Screenshare sharing not ready for primetime — the "share during
          meeting?" approval modal and the agenda↔page mapping panel are
          disabled. These never trigger now that uploads no longer set
          approvalAttachment/mappingAttachment.
      {approvalAttachment && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-lg font-semibold mb-2">Share this document during the meeting?</h3>
            <p className="text-sm text-gray-500 mb-4">{approvalAttachment.filename}</p>
            <div className="flex gap-3 justify-end flex-wrap">
              <button
                type="button"
                onClick={() => handleApprovalDecision('do_not_share')}
                className="btn-ghost text-sm text-gray-600"
              >
                No, keep private
              </button>
              <button
                type="button"
                onClick={() => handleApprovalDecision('not_decided')}
                className="btn-secondary text-sm"
              >
                Decide later
              </button>
              <button
                type="button"
                onClick={() => handleApprovalDecision('approved_for_meeting_share')}
                className="btn-primary text-sm"
              >
                Yes, share during meeting
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
      {mappingAttachment && (
        <AgendaDocumentMappingPanel
          meetingId={meetingId}
          attachmentId={mappingAttachment.id}
          filename={mappingAttachment.filename}
          pageCount={mappingAttachment.pageCount}
          onClose={() => setMappingAttachment(null)}
        />
      )}
      */}
      <div className={`flex flex-col gap-4 ${compact ? 'mb-2' : 'mb-4'}`}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            {showTitle ? (
              <h3
                className={`font-semibold text-gray-900 flex items-center gap-2 ${
                  compact ? 'text-sm' : 'text-lg'
                }`}
              >
                <FileText className={`text-primary-600 ${compact ? 'w-4 h-4' : 'w-5 h-5'}`} />
                {title}
              </h3>
            ) : null}
            {isUploadOnly && uploadOnlyHint ? (
              <p className="text-sm text-gray-500 mt-1">{uploadOnlyHint}</p>
            ) : null}
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                className="sr-only"
                aria-label="Upload meeting document"
                onChange={onUploadFileChange}
              />
              {/* Screenshare sharing not ready for primetime — hide the
                  "Share with all participants" option. Uploads remain enabled
                  for agenda generation.
              <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={shareUploadWithAll}
                  onChange={(ev) => setShareUploadWithAll(ev.target.checked)}
                  disabled={busy || uploading}
                />
                Share with all participants
              </label>
              */}
              <button
                type="button"
                disabled={busy || uploading || Boolean(shareApprovalWaitId)}
                onClick={() => fileInputRef.current?.click()}
                className="btn-secondary inline-flex items-center gap-2 text-sm disabled:opacity-60"
                title={
                  shareApprovalWaitId ? 'Finish preparing the last uploaded document before adding another.' : undefined
                }
              >
                {uploading || shareApprovalWaitId ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Upload document
              </button>
              {showIdeasChrome && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={runSuggestions}
                  className="btn-secondary inline-flex items-center gap-2 text-sm disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Generate Agenda Ideas
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 sm:text-right max-w-md">
              {formatsHint ? `${formatsHint} · ` : ''}Max 32 MB per file.
            </p>
          </div>
        </div>
        {shareApprovalWaitId ? (
          <p className="text-sm text-gray-600 flex items-start gap-2" role="status">
            <Loader2 className="w-4 h-4 shrink-0 animate-spin text-primary-600 mt-0.5" />
            <span className="min-w-0">
              <span className="block">
                {shareApprovalWaitBannerLine || 'Waiting for uploaded document…'}
              </span>
              <span className="block text-xs text-gray-400 truncate mt-0.5">
                {shareApprovalWaitFilenameRef.current || ''}
              </span>
            </span>
          </p>
        ) : null}
      </div>

      {!isUploadOnly && !isInlineButton &&
        (loading ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm py-8 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading materials…
          </div>
        ) : attachments.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No attachments for this view.</p>
        ) : (
          <ul className="space-y-3 mb-6">
            {attachments.map((a) => (
              <li key={a.id} className="rounded-xl border border-gray-100 bg-white/60 p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{a.filename || a.id}</p>
                    <p className="text-xs text-gray-500">
                      {a.processingStatus || a.processing_status || 'unknown'}
                      {a.shareWithAllParticipants || a.share_with_all_participants ? ' · shared' : ' · private'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {!isUploadOnly ? (
                      <button
                        type="button"
                        className="btn-ghost text-xs text-red-600 hover:text-red-700"
                        disabled={busy || deletingAttachmentId === a.id}
                        title="Remove this file from meeting documents (server soft-delete). You must be the uploader or an org admin."
                        onClick={() => removeAttachment(a.id, a.filename)}
                      >
                        {deletingAttachmentId === a.id ? (
                          <Loader2 className="w-4 h-4 animate-spin inline" />
                        ) : (
                          <Trash2 className="w-4 h-4 inline" />
                        )}{' '}
                        Delete
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ))}

      {!isUploadOnly && !isInlineButton && showAgendaSuggestions && suggestedAgendaLinks.length > 0 && (
        <div className="border-t border-gray-100 pt-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            From meeting prep (suggested agenda links)
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            Tick the ideas you want on the meeting agenda, then use the button below. Dismiss removes a suggestion from
            this list without adding it (you can generate fresh ideas again from prep).
          </p>
          <ul className="space-y-2">
            {suggestedAgendaLinks.map((l) => (
              <li
                key={l.linkId}
                className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 rounded-lg border border-gray-100 p-3 bg-white/70"
              >
                <label className="flex items-start gap-2 flex-1 min-w-0 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selectedApplyIds.has(l.linkId)}
                    onChange={() => toggleApply(l.linkId)}
                    disabled={busy}
                    aria-label={`Include “${l.proposedTitle}” when adding to agenda`}
                  />
                  <span className="min-w-0">
                    <span className="font-medium text-gray-900 block">{l.proposedTitle}</span>
                    <span className="text-xs text-gray-600 block mt-0.5">{l.rationale}</span>
                  </span>
                </label>
                <button
                  type="button"
                  className="shrink-0 text-sm text-gray-600 hover:text-gray-900 underline underline-offset-2 disabled:opacity-50 sm:pt-0.5"
                  disabled={busy}
                  title="Remove this suggestion without adding it to the agenda"
                  onClick={() => patchLink(l.linkId, 'rejected')}
                >
                  Dismiss
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            disabled={busy || selectedApplyIds.size === 0}
            onClick={applySelected}
            className="mt-3 btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Add selected to agenda
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Adds new agenda items for the ticked rows only — existing agenda lines are not changed.
          </p>
        </div>
      )}

      {isUploadOnly && loading && (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-3">
          <Loader2 className="w-4 h-4 animate-spin" />
          Checking document features…
        </div>
      )}
    </div>
  )
}
